import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractComponent, Webhook } from 'lib-trend-core';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'webhook-integrations-requests-view-edit',
    templateUrl: './webhook-integrations-requests-view.v2.component.html',
    // templateUrl: './webhook-integrations-requests-view.component.html',
    styleUrls: ['./webhook-integrations-requests-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WebhookIntegrationsRequestsViewComponent extends AbstractComponent {
  curlContent: string = '';
  url: string = '';
  viewMode: 'payload' | 'curl' = 'payload';
  webhookData: any;
  isSimpleMetadata: boolean = false;

  copied: boolean = false;

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(MAT_DIALOG_DATA) public webhook: Webhook,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.processIncomingData();
    this.generateCurl();
  }

  private processIncomingData(): void {
    if (!this.data) {
      console.error('Dados do webhook não encontrados!');
      return;
    }

    // Extrai os dados do webhook conforme o formato recebido
    if (this.data?.webhook) {
      // Formato novo: { webhook: {...}, url: '...' }
      this.webhookData = this.data.webhook;
      this.url = this.data.url || this.generateWebhookIntegrationsURLbyCode();
    } else {
      // Formato antigo: Webhook direto
      this.webhookData = this.data;
      this.url = this.generateWebhookIntegrationsURLbyCode();
    }

    // Determina se o metadata é simples (já é o body)
    this.isSimpleMetadata = this.checkIfSimpleMetadata();
  }

  private checkIfSimpleMetadata(): boolean {
    if (!this.webhookData.metadata) return false;

    // Se não tiver nenhum desses campos, assume que metadata já é o body
    return !('headers' in this.webhookData.metadata) &&
      !('query' in this.webhookData.metadata) &&
      !('body' in this.webhookData.metadata);
  }

  showPayload(): void {
    this.viewMode = 'payload';
  }

  showCurl(): void {
    this.generateCurl();
    this.viewMode = 'curl';
  }

  copyContent(): void {
    navigator.clipboard.writeText(this.curlContent).then(() => {
      this.copied = true;
      this.alertService.info(this.t('integrations.webhookRequestsView.infoContentCopied'));
      setTimeout(() => this.copied = false, 3000); // Reseta o estado após 3 segundos
    }).catch(err => {
      console.error('Erro ao copiar o conteúdo:', err);
      this.alertService.error(this.t('integrations.webhookRequestsView.errorCopyContent'));
    });
  }

  generateCurl(): void {
    let fullUrl = this.url;
    let headers: Record<string, string> = {};
    let body: any = null;

    if (this.isSimpleMetadata) {
      // Caso simples: metadata já é o body
      body = this.webhookData.metadata;
    } else {
      // Caso complexo: metadata tem estrutura completa
      const { metadata } = this.webhookData;

      // Processa query parameters
      const query = metadata?.query;
      if (query) {
        fullUrl += '?' + new URLSearchParams(query as any).toString();
      }

      // Processa headers
      if (metadata?.headers) {
        headers = { ...metadata.headers };
      }

      // Processa body
      body = metadata?.body;
    }

    // Constrói o comando curl
    const headerLines = Object.entries(headers)
      .map(([key, value]) => `  -H "${key}: ${value}" \\`)
      .join('\n');

    let curl = `curl -X POST "${fullUrl}" \\\n${headerLines}`;

    // Adiciona o body se existir
    if (body) {
      const jsonPayload = JSON.stringify(body, null, 2)
        .replace(/'/g, `'\\''`)
        .replace(/\n/g, '\n    ');
      curl += `\n  -d '${jsonPayload}'`;
    }

    this.curlContent = curl;
  }

  public generateWebhookIntegrationsURLbyCode(): string {
    const code = super.getCodeCompanyCurrentUser();

    // Tenta obter UUID de params (formato antigo)
    if (!this.isSimpleMetadata) {
      const params = this.webhookData.metadata?.params;
      if (params?.uuid) {
        return `${environment.trendIntegrationsUrl}/webhook/${code}/${params.uuid}`;
      }
    }

    // Tenta extrair UUID da URL se existir (formato novo)
    if (this.data?.url) {
      try {
        const urlObj = new URL(this.data.url);
        const pathParts = urlObj.pathname.split('/');
        const uuid = pathParts[pathParts.length - 1];
        if (uuid) {
          return `${environment.trendIntegrationsUrl}/webhook/${code}/${uuid}`;
        }
        return this.data.url;
      } catch {
        return this.data.url;
      }
    }

    // Fallback
    return `${environment.trendIntegrationsUrl}/webhook/${code}`;
  }

  get payloadToDisplay(): any {
    return this.isSimpleMetadata
      ? this.webhookData.metadata
      : this.webhookData.metadata?.body || this.webhookData.metadata;
  }
}
